import React, { useEffect, useState } from "react";
import {
  FileText,
  CheckCircle,
  Clock,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import textTranslator from "../../../utils/useTextTranslator";
import typeTranslator from "../../../utils/useTypeTranslator";
import { arClass } from "../../../utils/useArClass";
import Txt from "../../../components/Text";
import formatDate from "../../../utils/formatDate";
import formatTime from "../../../utils/formatTime";
import {
  apiFetchError,
  apiRequest,
  apiSuccess,
} from "../../../redux/api/apiActions";
import api from "../../../api";
import { useUser } from "../../../authentication/authentication";
import {
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaExclamationTriangle,
  FaFileAlt,
  FaGlobe,
  FaMoneyBill,
  FaPause,
  FaShieldAlt,
  FaTimesCircle,
  FaUndo,
  FaUniversity,
} from "react-icons/fa";

/**
 * Text Component Wrapper for Recent Bills Page
 * Props:
 * - children: Text content
 * - className: Additional CSS classes
 * @returns Text JSX
 */
const Text = ({ children, className, page = "PAYMENTS" }) => {
  return (
    <Txt page={page} className={className}>
      {children}
    </Txt>
  );
};

// Placeholder component for RecentBills
const RecentBillsPlaceholder = ({ darkMode = false }) => {
  return (
    <div
      className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                    bg-transparent shadow-none border-0 col-span-1 sm:col-span-2 lg:col-span-1
                    ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
    >
      {/* Title Placeholder with shimmer */}
      <div className={arClass("relative overflow-hidden mb-3.5")}>
        <div
          className={arClass(
            `h-5 w-32 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
          )}
        />
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      {/* Bill Items Placeholder */}
      <div className={arClass("flex flex-col")}>
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className={arClass(
              `relative overflow-hidden flex items-center justify-between py-2.5 ${i < 2 ? `border-b ${darkMode ? "border-gray-700" : "border-slate-100"}` : ""}`,
            )}
          >
            <div className={arClass("flex items-center gap-2.5 flex-1")}>
              {/* Icon Placeholder */}
              <div
                className={arClass(
                  `w-8 h-8 rounded-full ${darkMode ? "bg-gray-700" : "bg-gray-200"} flex-shrink-0`,
                )}
              />

              <div className={arClass("flex-1")}>
                {/* Bill ID Placeholder */}
                <div
                  className={arClass(
                    `h-4 w-20 rounded mb-1 ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
                  )}
                />
                {/* Date Placeholder */}
                <div
                  className={arClass(
                    `h-3 w-24 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
                  )}
                />
              </div>
            </div>

            <div className={arClass("text-right flex-shrink-0 ml-4")}>
              {/* Amount Placeholder */}
              <div
                className={arClass(
                  `h-4 w-16 rounded mb-1 ml-auto ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
                )}
              />
              {/* Status Badge Placeholder */}
              <div
                className={arClass(
                  `h-5 w-14 rounded-lg ml-auto ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
                )}
              />
            </div>

            {/* Shimmer overlay */}
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        ))}
      </div>

      {/* View All Button Placeholder */}
      <div className={arClass("relative overflow-hidden mt-3.5")}>
        <div
          className={arClass(
            `h-4 w-24 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
          )}
        />
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>
    </div>
  );
};

const RecentBills = () => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const { requests } = useSelector((state) => state.api);
  const { id } = useParams();
  const patientKey = `getPatient_${id}`;
  const paymentsKey = `getPaymentsByPatient_${id}`;
  const patientLoading = requests[patientKey]?.loading;
  const paymentsLoading = requests[paymentsKey]?.loading;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        dispatch(apiRequest("GET", paymentsKey));
        const response = await api.get(
          `/payments/patient-id/${id}?page=1&pageSize=5`,
        );

        setPayments(response.data.results || response.data);
        dispatch(
          apiSuccess("GET", paymentsKey, "Fetched payments successfully"),
        );
      } catch (error) {
        console.error("Error fetching payments:", error);
        setError(error.message || "Failed to fetch payments");
        dispatch(apiFetchError("GET", paymentsKey, error.message));
      }
    };

    if (id) {
      fetchPayments();
    }
  }, [id, dispatch, paymentsKey]);

  /**
   * Badge class mappings for payment status
   * @returns Badge class object
   */
  const statusBadgeClass = (darkMode, status) => {
    const defaultClasses = {
      paid: darkMode
        ? "bg-emerald-800 !text-emerald-100 border border-emerald-700"
        : "bg-emerald-50 !text-emerald-600 border border-emerald-200",
      pending: darkMode
        ? "bg-amber-800 !text-amber-100 border border-amber-700"
        : "bg-amber-50 !text-amber-600 border border-amber-200",
      failed: darkMode
        ? "bg-rose-800 !text-rose-100 border border-rose-700"
        : "bg-rose-50 !text-rose-600 border border-rose-200",
      cancelled: darkMode
        ? "bg-gray-800 !text-gray-100 border border-gray-700"
        : "bg-gray-50 !text-gray-600 border border-gray-200",
      refunded: darkMode
        ? "bg-blue-800 !text-blue-100 border border-blue-700"
        : "bg-blue-50 !text-blue-600 border border-blue-200",
    };
    return (
      defaultClasses[status] ||
      (darkMode
        ? "bg-slate-800 !text-slate-100 border border-slate-700"
        : "bg-slate-50 !text-slate-600 border border-slate-200")
    );
  };

  const handleViewAll = () => {
    navigate("/bills");
  };

  // Format payment amount with currency
  const formatAmount = (amount) => {
    return `${parseFloat(amount).toFixed(2)} ${textTranslator("MAD", "PAYMENTS")}`;
  };

  // Format date for display
  const formatPaymentDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(defaultLanguage, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Generate bill ID from payment ID
  const getBillId = (paymentId) => {
    return `#${String(paymentId).padStart(4, "0")}`;
  };

  // Get payment method display name
  const getPaymentMethodDisplay = (method) => {
    const methodMap = {
      cash: "Cash",
      card: "Card",
      bank_transfer: "Bank Transfer",
      insurance: "Insurance",
      online: "Online Payment",
    };
    return methodMap[method] || method;
  };

  // Show placeholder while loading
  if (patientLoading || paymentsLoading) {
    return <RecentBillsPlaceholder darkMode={darkMode} />;
  }

  // Show empty state if no payments
  if (!payments || payments.length === 0) {
    return (
      <div
        className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                      bg-transparent shadow-none border-0 col-span-1 sm:col-span-2 lg:col-span-1
                      ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
      >
        <h3
          className={arClass(
            `mb-3.5 text-[13px] font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`,
          )}
        >
          <Text>Recent Bills</Text>
        </h3>
        <div
          className={arClass("flex flex-col items-center justify-center py-8")}
        >
          <FileText
            className={arClass(
              `w-12 h-12 ${darkMode ? "text-gray-600" : "text-gray-300"} mb-3`,
            )}
          />
          <p
            className={arClass(
              `text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`,
            )}
          >
            <Text>No recent bills</Text>
          </p>
        </div>
      </div>
    );
  }

  const getPaymentStatusLabel = (payment) => {
    if (!payment) {
      return {
        label: textTranslator("No Payment Information", "PAYMENTS"),
        textColor: darkMode ? "text-gray-300" : "text-gray-900",
        style: darkMode
          ? "status !bg-gray-700 !text-gray-300 shadow-sm border border-gray-600  text-xs"
          : "status !bg-gray-300 !text-gray-900 shadow-sm border border-gray-900/40  text-xs",
      };
    }

    const lowerStatus = (payment.status || "").toLowerCase();

    switch (lowerStatus) {
      case "paid":
      case "completed":
        return {
          label: textTranslator("paid", "PAYMENTS"),
          textColor: darkMode ? "text-green-300" : "text-green-700",
          style: darkMode
            ? "status !bg-green-900/30 !text-green-300 shadow-sm border border-green-700  text-xs"
            : "status !bg-green-50 !text-green-700 shadow-sm border border-green-200  text-xs",
        };

      case "pending":
        return {
          label: textTranslator("pending", "PAYMENTS"),
          textColor: darkMode ? "text-orange-300" : "text-orange-700",
          style: darkMode
            ? "status !bg-orange-900/30 !text-orange-300 shadow-sm border border-orange-700  text-xs"
            : "status !bg-orange-50 !text-orange-700 shadow-sm border border-orange-200  text-xs",
        };

      case "failed":
        return {
          label: textTranslator("failed", "PAYMENTS"),
          textColor: darkMode ? "text-red-300" : "text-red-700",
          style: darkMode
            ? "status !bg-red-900/30 !text-red-300 shadow-sm border border-red-700  text-xs"
            : "status !bg-red-50 !text-red-700 shadow-sm border border-red-200  text-xs",
        };

      case "refunded":
        return {
          label: textTranslator("refunded", "PAYMENTS"),
          textColor: darkMode ? "text-indigo-300" : "text-indigo-700",
          style: darkMode
            ? "status !bg-indigo-900/30 !text-indigo-300 shadow-sm border border-indigo-700  text-xs"
            : "status !bg-indigo-50 !text-indigo-700 shadow-sm border border-indigo-200  text-xs",
        };

      case "partially-paid":
      case "partially_paid":
      case "partial":
      case "partially paid":
        return {
          label: textTranslator("partially paid", "PAYMENTS"),
          textColor: darkMode ? "text-yellow-300" : "text-yellow-700",
          style: darkMode
            ? "status !bg-yellow-900/30 !text-yellow-300 shadow-sm border border-yellow-700  text-xs"
            : "status !bg-yellow-50 !text-yellow-700 shadow-sm border border-yellow-200  text-xs",
        };

      case "cancelled":
      case "canceled":
        return {
          label: textTranslator("cancelled", "PAYMENTS"),
          textColor: darkMode ? "text-red-300" : "text-red-700",
          style: darkMode
            ? "status !bg-red-900/30 !text-red-300 shadow-sm border border-red-700  text-xs"
            : "status !bg-red-50 !text-red-700 shadow-sm border border-red-200  text-xs",
        };

      case "on-hold":
      case "on_hold":
      case "hold":
        return {
          label: textTranslator("on-hold", "PAYMENTS"),
          textColor: darkMode ? "text-gray-300" : "text-gray-800",
          style: darkMode
            ? "status !bg-gray-800/30 !text-gray-300 shadow-sm border border-gray-600  text-xs"
            : "status !bg-gray-100 !text-gray-800 shadow-sm border border-gray-200  text-xs",
        };

      default:
        return {
          label: textTranslator("unknown", "PAYMENTS"),
          textColor: darkMode ? "text-gray-300" : "text-gray-900",
          style: darkMode
            ? "bg-gray-700 text-gray-300"
            : "bg-gray-500 text-white",
        };
    }
  };

  const getMethod = (method) => {
    const methodConfig = {
      cash: {
        label: textTranslator("cash", "PAYMENTS"),
        value: "cash",
        icon: <FaMoneyBill className="inline" size={14} />,
      },
      "credit-card": {
        label: textTranslator("credit card", "PAYMENTS"),
        value: "credit-card",
        icon: <FaCreditCard className="inline" size={14} />,
      },
      "debit-card": {
        label: textTranslator("debit card", "PAYMENTS"),
        value: "debit-card",
        icon: <FaUniversity className="inline" size={14} />,
      },
      insurance: {
        label: textTranslator("insurance", "PAYMENTS"),
        value: "insurance",
        icon: <FaShieldAlt className="inline" size={14} />,
      },
      "online-payment": {
        label: textTranslator("online payment", "PAYMENTS"),
        value: "online-payment",
        icon: <FaGlobe className="inline" size={14} />,
      },
      other: {
        label: textTranslator("other", "PAYMENTS"),
        value: "other",
        icon: <FaFileAlt className="inline" size={14} />,
      },
    };

    const lowerMethod = method?.toLowerCase();
    const config = methodConfig[lowerMethod] || methodConfig.other;

    // Dark mode styles
    const darkModeStyles = {
      cash: "bg-green-900/30 text-green-300 border border-green-700",
      "credit-card": "bg-blue-900/30 text-blue-300 border border-blue-700",
      "debit-card": "bg-purple-900/30 text-purple-300 border border-purple-700",
      insurance: "bg-orange-900/30 text-orange-300 border border-orange-700",
      "online-payment": "bg-cyan-900/30 text-cyan-300 border border-cyan-700",
      other: "bg-gray-700 text-gray-300 border border-gray-600",
    };

    // Light mode styles
    const lightModeStyles = {
      cash: "bg-green-50 text-green-700 border border-green-200",
      "credit-card": "bg-blue-50 text-blue-700 border border-blue-200",
      "debit-card": "bg-purple-50 text-purple-700 border border-purple-200",
      insurance: "bg-orange-50 text-orange-700 border border-orange-200",
      "online-payment": "bg-cyan-50 text-cyan-700 border border-cyan-200",
      other: "bg-gray-50 text-gray-700 border border-gray-200",
    };

    const style = darkMode
      ? darkModeStyles[lowerMethod] || darkModeStyles.other
      : lightModeStyles[lowerMethod] || lightModeStyles.other;

    return {
      ...config,
      style: `p-2 rounded-full shadow-sm text-sm ${style}`,
    };
  };

  return (
    <div
      className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                    bg-transparent shadow-none border-0 col-span-1 sm:col-span-2 lg:col-span-1
                    ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
    >
      <h3
        className={arClass(
          `mb-3.5 text-[13px] font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`,
        )}
      >
        <Text>Recent Bills</Text>
      </h3>

      <div className={arClass("flex flex-col")}>
        {payments.map((payment, i) => {
         
          return (
            <div
                        key={i}
                        className={arClass(
                          `flex items-center justify-between py-2.5 ${i < payments.length - 1 ? `border-b ${darkMode ? "border-gray-700" : "border-slate-50"}` : ""}`,
                        )}
                      >
              <div className={arClass("flex items-center gap-2.5 flex-1")}>
                <div
                  className={arClass(
                    `w-8 h-8 flex items-center justify-center ${getMethod(payment.method).style}`,
                  )}
                >
                  {getMethod(payment.method).icon}
                </div>
                <div>
                  <p
                    className={arClass(
                      `text-[13px] font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`,
                    )}
                  >
                    {getBillId(payment.id)}
                  </p>
                  <p
                    className={arClass(
                      `text-[11px] ${darkMode ? "text-gray-500" : "text-gray-400"}`,
                    )}
                  >
                    {formatPaymentDate(
                      payment.paymentDate || payment.createdAt,
                    )}
                  </p>
                </div>
              </div>

              <div className={arClass("flex flex-col items-end gap-0.5")}>
                <p
                  className={arClass(
                    `text-[13px] font-bold ${darkMode ? "text-gray-100" : "text-gray-900"} w-full text-right`,
                  )}
                >
                  {formatAmount(payment.amount)}
                </p>
                <span
                  className={arClass(
                    `rounded-lg px-2 py-0.5 text-[10px] font-semibold w-full text-right flex items-center justify-center ${getPaymentStatusLabel(payment).style}`,
                  )}
                >
                  {getPaymentStatusLabel(payment).label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <button
        className={arClass(
          `mt-3.5 flex items-center gap-1 text-[12px] font-medium text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors`,
        )}
        onClick={handleViewAll}
      >
        <Text>View all bills</Text>
        {defaultLanguage === "ar" ? (
          <ChevronLeft className={arClass("w-3 h-3")} />
        ) : (
          <ChevronRight className={arClass("w-3 h-3")} />
        )}
      </button>
    </div>
  );
};

export default RecentBills;
